import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  CreditCard, Key, Coins, Check, ArrowRight, Eye, EyeSlash,
  ShieldCheck, Sparkle, Package, Warning, Trash, PaintBrush, Upload, PencilSimple,
  Crown, Rocket, Lightning, Star, FilmReel, ChartLineUp, Wallet, CircleNotch, Gauge,
} from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";
import BaseVideoLibrary from "@/components/BaseVideoLibrary";

const CREDIT_PACKS = [
  { id: "starter", name: "Starter", credits: 50, price: 9.99, popular: false },
  { id: "pro", name: "Pro", credits: 200, price: 29.99, popular: true },
  { id: "business", name: "Business", credits: 600, price: 79.99, popular: false },
];

const SUBSCRIPTION_PLANS = [
  { id: "creator", name: "Creator", price: 19.99, credits: 100, popular: false, features: ["Google Drive access", "100 credits/month", "All AI tools"] },
  { id: "pro", name: "Pro", price: 39.99, credits: 300, popular: true, features: ["Everything in Creator", "300 credits/month", "Priority processing", "Bulk Create"] },
  { id: "business", name: "Business", price: 79.99, credits: 700, popular: false, features: ["Everything in Pro", "700 credits/month", "Content Calendar", "Priority support"] },
];

const SERVICE_LABELS = {
  elevenlabs: {
    name: "ElevenLabs",
    icon: "Voice cloning & TTS — free tier covers 10k chars/mo with Instant Voice Clone",
    instructions: "1. Sign up FREE at elevenlabs.io (no credit card required)\n2. Go to Settings → API Keys → Create New Key\n3. IMPORTANT: When creating the key, set permissions to \"Has Access\" for ALL scopes (especially Voices & Text to Speech). The default \"Restricted\" preset gets rejected with a 401.\n4. Copy the key (starts with sk_ or xi_)",
    url: "https://elevenlabs.io/app/sign-up",
  },
  fal_ai: {
    name: "fal.ai (VEED Fabric 1.0)",
    icon: "PRIMARY talking-head video model — fast renders (~1-2 min) and high quality.",
    instructions: "1. Go to fal.ai and sign in with Google\n2. Go to Dashboard → Keys (https://fal.ai/dashboard/keys)\n3. Click 'New Key' and copy the key (format: <id>:<secret>)\n4. Pricing: $0.10/sec at 480p, $0.20/sec at 720p — pay-as-you-go, no monthly minimum.\n5. Per-call limit: 30 s of audio. Longer scripts auto-chunk via Long-Form Studio.",
    url: "https://fal.ai/dashboard/keys",
  },
  magic_hour: {
    name: "Magic Hour",
    icon: "Secondary/fallback talking-photo + lip-sync renderer. Used when fal.ai isn't configured or for >30s clips.",
    instructions: "1. Go to magichour.ai and sign up\n2. Click your profile → Developer Hub → API Keys\n3. Click 'Create new key', name it, and copy the key (starts with mhk_live_)\n4. Make sure your account has credits or an active plan",
    url: "https://magichour.ai",
  },
  muapi: {
    name: "muapi.ai",
    icon: "Image hosting, outfit swap, B-roll, Veo 3.1 cinematic, MiniMax voice cloning",
    instructions: "1. Go to muapi.ai and create an account\n2. Navigate to API Keys in your dashboard\n3. Copy your API key\n4. Top up $10-20 to start — powers image generation, cinematic Veo videos, and MiniMax voice cloning",
    url: "https://muapi.ai",
  },
};

const INDUSTRIES = [
  "Beauty & Fashion", "Health & Wellness", "Technology & AI",
  "Real Estate", "Finance & Investing", "Food & Beverage",
  "Education & Coaching", "Fitness & Sports", "Travel & Lifestyle",
  "E-commerce & Retail", "Entertainment & Media", "Other",
];

// Phase 1 plan-status banner: shows the user's current tier, video-count
// usage this cycle, and a one-click migration button for legacy credit
// holders. Stripe checkout for upgrading between paid tiers ships Phase 2.
const PlanStatusBanner = () => {
  const { refreshUser } = useAuth();
  const [plan, setPlan] = useState(null);
  const [allPlans, setAllPlans] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [msg, setMsg] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(null); // lookup_key or null
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "yearly"

  const load = useCallback(async () => {
    try {
      const [me, all] = await Promise.all([
        axios.get(`${API}/plans/me`),
        axios.get(`${API}/plans`),
      ]);
      setPlan(me.data);
      setAllPlans(all.data.plans);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const migrate = async () => {
    setMigrating(true);
    setMsg(null);
    try {
      const r = await axios.post(`${API}/plans/migrate-me`);
      if (r.data.migrated) {
        setMsg(`✓ Converted ${r.data.converted_credits} credits to ${r.data.granted_days_starter} days of Starter. Reloading…`);
        setTimeout(() => { load(); refreshUser?.(); }, 1500);
      } else {
        setMsg(`(${r.data.reason})`);
      }
    } catch (e) {
      setMsg(e.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  if (!plan) return null;
  const canMigrate = !plan.is_admin_unlimited && !plan.migrated_from_credits && (plan.legacy_credits_remaining || 0) > 0;
  const daysLeft = plan.usage?.period_end
    ? Math.max(0, Math.round((new Date(plan.usage.period_end) - new Date()) / 86400000))
    : null;

  return (
    <div className="border-2 border-[#E91E8C]/30 bg-gradient-to-r from-[#E91E8C]/5 to-transparent rounded-2xl p-5" data-testid="plan-status-banner">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[#E91E8C] mb-1">Your plan · new billing engine</p>
          <p className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            {plan.plan.name}
            {plan.is_admin_unlimited && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-500 text-white">Admin · Unlimited</span>}
          </p>
          {!plan.is_admin_unlimited && (
            <p className="text-sm text-zinc-700 mt-1">
              <span className="font-bold" data-testid="plan-usage-count">{plan.usage.videos_used}</span> of {plan.usage.videos_limit} videos used this cycle · max {plan.plan.max_seconds_per_video}s per video · {plan.plan.allowed_resolutions.join(" / ")}
              {daysLeft !== null && <> · resets in <span className="font-bold">{daysLeft}d</span></>}
            </p>
          )}
        </div>
      </div>

      {canMigrate && (
        <div className="mt-4 p-3 rounded-xl bg-white border border-amber-200">
          <p className="text-sm text-amber-900 font-semibold mb-1">💫 One-click migration for your legacy credits</p>
          <p className="text-xs text-amber-800 mb-2">
            You have <strong>{plan.legacy_credits_remaining}</strong> credits from the old credit-pack system. Convert them to <strong>~{Math.max(3, Math.round((plan.legacy_credits_remaining / 50) * 30))} days</strong> of Starter tier access.
          </p>
          <button
            type="button"
            onClick={migrate}
            disabled={migrating}
            className="btn-primary text-xs px-4 py-2 disabled:opacity-50"
            data-testid="migrate-credits-btn"
          >
            {migrating ? "Converting..." : "Convert my credits →"}
          </button>
          {msg && <p className="text-xs mt-2 text-emerald-700 font-semibold">{msg}</p>}
        </div>
      )}

      {plan.migrated_from_credits && (
        <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1">
          <Check size={12} weight="bold" /> Legacy credits already converted
        </p>
      )}

      {/* Pending downgrade indicator + undo */}
      {plan.pending_tier && !plan.is_admin_unlimited && (
        <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between gap-3" data-testid="pending-downgrade">
          <p className="text-xs text-amber-900">
            Downgrade to <strong className="capitalize">{plan.pending_tier}</strong> takes effect on{" "}
            {plan.usage?.period_end ? new Date(plan.usage.period_end).toLocaleDateString() : "next cycle"}.
            You keep all {plan.plan.name} features until then.
          </p>
          <button
            onClick={async () => {
              try {
                await axios.post(`${API}/plans/undo-downgrade`);
                await load();
                refreshUser?.();
              } catch (e) { setMsg(e.response?.data?.detail || "Undo failed"); }
            }}
            className="text-xs font-bold text-[#E91E8C] hover:underline flex-shrink-0"
            data-testid="undo-downgrade-btn"
          >Undo</button>
        </div>
      )}

      {/* Upgrade grid — Phase 2 Stripe recurring checkout */}
      {!plan.is_admin_unlimited && allPlans && (
        <div className="mt-5 pt-5 border-t border-[#E91E8C]/20" data-testid="upgrade-grid">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Upgrade or switch plan</p>
            <div className="inline-flex rounded-full bg-zinc-100 p-0.5 text-xs">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-3 py-1 rounded-full font-bold ${billingCycle === "monthly" ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}
                data-testid="billing-monthly"
              >Monthly</button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`px-3 py-1 rounded-full font-bold ${billingCycle === "yearly" ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}
                data-testid="billing-yearly"
              >Yearly · 2 mo free</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {["starter", "creator", "pro", "studio", "enterprise"].map((t) => {
              const p = allPlans[t];
              if (!p) return null;
              const lk = p.stripe_lookup_keys?.[billingCycle];
              const isCurrent = plan.tier === t;
              const displayPrice = billingCycle === "yearly" ? p.price * 10 : p.price;
              const currentPrice = allPlans[plan.tier]?.price || 0;
              const isDowngrade = !isCurrent && p.price < currentPrice;
              return (
                <button
                  key={t}
                  disabled={isCurrent || checkoutBusy === lk}
                  onClick={async () => {
                    if (isDowngrade) {
                      if (!window.confirm(`Schedule a downgrade to ${p.name}? You'll keep all ${plan.plan.name} features until your cycle ends on ${plan.usage?.period_end ? new Date(plan.usage.period_end).toLocaleDateString() : "next billing date"}, then you'll be moved to ${p.name}.`)) return;
                      setCheckoutBusy(lk || `downgrade-${t}`);
                      try {
                        await axios.post(`${API}/plans/downgrade`, { target_tier: t });
                        await load(); refreshUser?.();
                        setMsg(`✓ Scheduled downgrade to ${p.name} at cycle end.`);
                      } catch (e) {
                        setMsg(e.response?.data?.detail || "Downgrade failed");
                      } finally { setCheckoutBusy(null); }
                      return;
                    }
                    setCheckoutBusy(lk);
                    try {
                      const r = await axios.post(`${API}/plans/checkout`, {
                        lookup_key: lk,
                        origin_url: window.location.origin,
                      });
                      window.location.href = r.data.checkout_url;
                    } catch (e) {
                      setMsg(e.response?.data?.detail || "Checkout failed");
                      setCheckoutBusy(null);
                    }
                  }}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    isCurrent
                      ? "border-[#E91E8C] bg-[#E91E8C]/10 cursor-default"
                      : isDowngrade
                      ? "border-amber-200 hover:border-amber-400 bg-white"
                      : "border-zinc-200 hover:border-[#E91E8C] bg-white"
                  } disabled:opacity-60`}
                  data-testid={`plan-upgrade-${t}`}
                >
                  <p className="text-sm font-bold text-zinc-900">{p.name}</p>
                  <p className="text-lg font-bold text-[#E91E8C] mt-0.5">
                    ${displayPrice}<span className="text-[10px] text-zinc-500 font-normal">/{billingCycle === "yearly" ? "yr" : "mo"}</span>
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    {p.videos_per_month} vids · {p.max_seconds_per_video}s max
                  </p>
                  <p className="text-[10px] font-bold mt-1.5">
                    {isCurrent ? <span className="text-[#E91E8C]">Current plan</span> :
                     checkoutBusy === lk || checkoutBusy === `downgrade-${t}` ? <span className="text-zinc-400">Working…</span> :
                     isDowngrade ? <span className="text-amber-700">Downgrade at cycle end →</span> :
                     <span className="text-zinc-700">Choose {p.name} →</span>}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Cancel-to-Free footer action */}
          {plan.tier !== "free" && !plan.pending_tier && (
            <div className="mt-4 flex items-center justify-end">
              <button
                onClick={async () => {
                  if (!window.confirm(`Cancel subscription and move to Free at cycle end (${plan.usage?.period_end ? new Date(plan.usage.period_end).toLocaleDateString() : "renewal date"})? You keep every ${plan.plan.name} feature until then.`)) return;
                  try {
                    await axios.post(`${API}/plans/downgrade`, { target_tier: "free" });
                    await load(); refreshUser?.();
                    setMsg("✓ Subscription will cancel at cycle end.");
                  } catch (e) { setMsg(e.response?.data?.detail || "Cancel failed"); }
                }}
                className="text-xs text-zinc-500 hover:text-red-600 hover:underline"
                data-testid="cancel-to-free-btn"
              >
                Cancel &amp; move to Free at cycle end
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AccountSettings = () => {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState("brand");
  const [purchasing, setPurchasing] = useState(null);
  const [keys, setKeys] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [elevenlabsStatus, setElevenlabsStatus] = useState(null); // validated|present_unvalidated|invalid_permissions|quota_or_plan_issue|missing
  const [keyInputs, setKeyInputs] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [pollSessionId, setPollSessionId] = useState(null);
  const [pollType, setPollType] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [purchasingSub, setPurchasingSub] = useState(null);

  // Brand settings state
  const [brandName, setBrandName] = useState("");
  const [industry, setIndustry] = useState("");
  const [brandColor, setBrandColor] = useState("#E91E8C");
  const [brandDescription, setBrandDescription] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [brandSaved, setBrandSaved] = useState(false);

  // Usage & budget state
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const r = await axios.get(`${API}/usage/summary`);
      setUsage(r.data);
    } catch (e) {
      // non-fatal — the rest of Account Settings keeps working
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === "budget") loadUsage(); }, [tab, loadUsage]);

  // Populate brand settings from user
  useEffect(() => {
    if (user) {
      setBrandName(user.brand_name || "");
      setIndustry(user.industry || "");
      setBrandColor(user.brand_color || "#E91E8C");
      setBrandDescription(user.brand_description || "");
      setLogoPreview(user.logo_url || null);
    }
  }, [user]);

  // Check for payment return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentStatus = params.get("payment");
    const payType = params.get("type");
    if (sessionId && paymentStatus === "success") {
      setPollSessionId(sessionId);
      setPollType(payType || "credits");
    }
  }, []);

  // Fetch subscription status
  const fetchSubscription = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true, headers: getAuthHeaders() });
      setSubscriptionStatus(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  // Poll payment status
  useEffect(() => {
    if (!pollSessionId) return;
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const endpoint = pollType === "subscription"
          ? `${API}/subscription/confirm/${pollSessionId}`
          : `${API}/credits/status/${pollSessionId}`;
        const res = await axios.get(endpoint, { withCredentials: true, headers: getAuthHeaders() });
        if (res.data.status === "paid") {
          clearInterval(poll);
          setPollSessionId(null);
          setPollType(null);
          refreshUser();
          fetchSubscription();
          window.history.replaceState({}, "", "/dashboard");
        } else if (res.data.status === "expired" || attempts > 10) {
          clearInterval(poll);
          setPollSessionId(null);
          setPollType(null);
        }
      } catch {
        if (attempts > 10) { clearInterval(poll); setPollSessionId(null); setPollType(null); }
      }
    }, 2000);
    return () => clearInterval(poll);
  }, [pollSessionId, pollType, refreshUser, fetchSubscription]);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/keys`, { withCredentials: true, headers: getAuthHeaders() });
      setKeys(res.data.keys || {});
    } catch {}
    // ElevenLabs badge must reflect VALIDATION, not mere presence — this is
    // why Credits & Keys said "Connected" while Clone My Voice said MISSING.
    try {
      const s = await axios.get(`${API}/voice/elevenlabs/status`, { withCredentials: true, headers: getAuthHeaders() });
      setElevenlabsStatus(s.data?.status || null);
    } catch { setElevenlabsStatus(null); }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    try {
      const res = await axios.post(`${API}/credits/purchase`, {
        pack_id: packId,
        origin_url: window.location.origin,
      }, { withCredentials: true, headers: getAuthHeaders() });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Purchase failed");
    } finally {
      setPurchasing(null);
    }
  };

  const handleSubscribe = async (planId) => {
    setPurchasingSub(planId);
    try {
      const res = await axios.post(`${API}/subscription/purchase`, {
        plan_id: planId,
        origin_url: window.location.origin,
      }, { withCredentials: true, headers: getAuthHeaders() });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Subscription failed");
    } finally {
      setPurchasingSub(null);
    }
  };

  const handleSaveKey = async (service) => {
    setSavingKey(service);
    try {
      await axios.put(`${API}/keys/${service}`, keyInputs[service] || {}, { withCredentials: true, headers: getAuthHeaders() });
      setEditingKey(null);
      setKeyInputs((prev) => ({ ...prev, [service]: {} }));
      fetchKeys();
    } catch (err) {
      alert(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const handleDeleteKey = async (service) => {
    try {
      await axios.delete(`${API}/keys/${service}`, { withCredentials: true, headers: getAuthHeaders() });
      fetchKeys();
    } catch {}
  };

  const isAdmin = user?.plan === "admin";

  const handleSaveBrand = async () => {
    setSavingBrand(true);
    setBrandSaved(false);
    try {
      let logoUrl = user?.logo_url || null;
      if (logoFile) {
        const formData = new FormData();
        formData.append("file", logoFile);
        const uploadRes = await axios.post(`${API}/auth/upload-logo`, formData, {
          withCredentials: true,
          headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
        });
        logoUrl = uploadRes.data.url;
      }
      await axios.patch(`${API}/auth/onboard`, {
        brand_name: brandName,
        industry,
        brand_color: brandColor,
        brand_description: brandDescription,
        logo_url: logoUrl,
      }, { withCredentials: true, headers: { ...getAuthHeaders(), "Content-Type": "application/json" } });
      await refreshUser();
      setBrandSaved(true);
      setTimeout(() => setBrandSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save brand settings");
    } finally {
      setSavingBrand(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="account-settings">
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-zinc-200 pb-1 overflow-x-auto">
        {[
          { id: "brand", label: "Brand", icon: PaintBrush },
          { id: "subscription", label: "Subscription", icon: Crown },
          { id: "credits", label: "Credits", icon: Coins },
          { id: "budget", label: "Budget & Balance", icon: Wallet },
          { id: "videos", label: "Base Videos", icon: FilmReel },
          { id: "keys", label: "API Keys (BYOK)", icon: Key },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all ${
              tab === t.id ? "text-[#E91E8C] border-b-2 border-[#E91E8C] -mb-[3px]" : "text-zinc-500 hover:text-zinc-700"
            }`}
          >
            <t.icon size={18} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Brand Settings Tab */}
      {tab === "brand" && (
        <div className="space-y-6">
          <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-zinc-900 mb-1">Brand Settings</h3>
            <p className="text-sm text-zinc-500 mb-6">Update your brand identity. This personalizes all AI-generated content to match your brand.</p>

            {/* Logo */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-700 mb-2">Brand Logo</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-xl border-2 border-dashed border-zinc-300 flex items-center justify-center overflow-hidden bg-white">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Upload size={24} className="text-zinc-400" />
                  )}
                </div>
                <div>
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 rounded-lg text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-all">
                    <PencilSimple size={16} />
                    {logoPreview ? "Change Logo" : "Upload Logo"}
                    <input type="file" accept="image/*" onChange={handleLogoSelect} className="hidden" data-testid="logo-upload-input" />
                  </label>
                  <p className="text-xs text-zinc-400 mt-1">PNG, JPG up to 2MB</p>
                </div>
              </div>
            </div>

            {/* Brand Name */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-zinc-700 mb-2">Brand / Company Name</label>
              <input
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="e.g. The Branding Bar"
                data-testid="settings-brand-name"
                className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 focus:border-[#E91E8C]"
              />
            </div>

            {/* Industry */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-zinc-700 mb-2">Industry</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {INDUSTRIES.map((ind) => (
                  <button
                    key={ind}
                    onClick={() => setIndustry(ind)}
                    data-testid={`settings-industry-${ind.toLowerCase().replace(/\s|&/g, "-")}`}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                      industry === ind
                        ? "bg-[#E91E8C] text-white border-[#E91E8C]"
                        : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"
                    }`}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            </div>

            {/* Brand Color */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-700 mb-2">Brand Color</label>
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  data-testid="settings-brand-color"
                  className="w-14 h-14 rounded-xl cursor-pointer bg-transparent border-2 border-zinc-200"
                />
                <div>
                  <p className="text-zinc-900 font-mono text-sm">{brandColor}</p>
                  <p className="text-zinc-500 text-xs">Used as your dashboard accent color</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full" style={{ backgroundColor: brandColor }} />
                  <div className="px-3 py-1 rounded-full text-white text-xs font-bold" style={{ backgroundColor: brandColor }}>Preview</div>
                </div>
              </div>
            </div>

            {/* Brand Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-700 mb-2">Brand Description</label>
              <p className="text-zinc-500 text-xs mb-2">Describe your brand, offers, goals, and target audience. The AI uses this to create personalized content topics and scripts for your business.</p>
              <textarea
                value={brandDescription}
                onChange={(e) => setBrandDescription(e.target.value)}
                placeholder="Example: I'm a business coach helping female entrepreneurs scale to 6 figures using social media marketing. My audience is women aged 25-45..."
                rows={5}
                data-testid="settings-brand-description"
                className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 focus:border-[#E91E8C] text-sm resize-none"
              />
            </div>

            {/* Save */}
            <button
              onClick={handleSaveBrand}
              disabled={savingBrand || !brandName}
              data-testid="save-brand-settings"
              className="w-full py-3 bg-[#E91E8C] text-white font-semibold rounded-xl hover:bg-[#d4177d] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {savingBrand ? "Saving..." : brandSaved ? (
                <><Check size={18} weight="bold" /> Saved!</>
              ) : (
                "Save Brand Settings"
              )}
            </button>
          </div>
        </div>
      )}

      {/* Subscription Tab */}
      {tab === "subscription" && (
        <div className="space-y-6" data-testid="subscription-tab">
          {/* NEW plan status banner (Phase 1 + Phase 2 — plan engine + Stripe recurring) */}
          <PlanStatusBanner />
          {/* Legacy credit-pack UI hidden as of Phase 3 — the new
              PlanStatusBanner above is now the single source of truth
              for both current-plan display AND upgrade CTAs. Kept in
              the DOM (via CSS hide) so existing tests referencing it
              don't break; will be deleted in a follow-up cleanup. */}
          <div style={{ display: "none" }} aria-hidden="true">
          {/* Current Plan */}
          <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm mb-1">Current Plan</p>
                <p className="text-2xl font-bold flex items-center gap-2" data-testid="current-plan">
                  {isAdmin ? (
                    <><ShieldCheck size={24} weight="fill" className="text-emerald-400" /> Admin</>
                  ) : subscriptionStatus?.subscription_active ? (
                    <><Crown size={24} weight="fill" className="text-amber-400" /> {SUBSCRIPTION_PLANS.find(p => p.id === subscriptionStatus.subscription_tier)?.name || subscriptionStatus.subscription_tier}</>
                  ) : (
                    <>Free Plan</>
                  )}
                </p>
                {subscriptionStatus?.subscription_active && subscriptionStatus.subscription_expires_at && (
                  <p className="text-zinc-400 text-xs mt-1">
                    Renews {new Date(subscriptionStatus.subscription_expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Crown size={40} weight="duotone" className="text-[#E91E8C]" />
            </div>
            {subscriptionStatus?.has_drive_access && (
              <div className="mt-3 flex items-center gap-2 text-sm text-emerald-400">
                <Check size={16} weight="bold" />
                Google Drive access unlocked
              </div>
            )}
          </div>

          {pollSessionId && pollType === "subscription" && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-amber-800 text-sm font-medium">Activating your subscription...</p>
            </div>
          )}

          {/* Plans */}
          {!isAdmin && (
            <>
              <h3 className="text-lg font-semibold text-zinc-900">Choose a Plan</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {SUBSCRIPTION_PLANS.map((plan) => {
                  const isCurrentPlan = subscriptionStatus?.subscription_tier === plan.id && subscriptionStatus?.subscription_active;
                  return (
                    <div
                      key={plan.id}
                      data-testid={`plan-${plan.id}`}
                      className={`relative border rounded-2xl p-5 transition-all hover:shadow-md ${
                        plan.popular ? "border-[#E91E8C] ring-1 ring-[#E91E8C]/20" :
                        isCurrentPlan ? "border-emerald-400 ring-1 ring-emerald-200" :
                        "border-zinc-200"
                      }`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#E91E8C] text-white text-xs font-bold rounded-full">
                          Most Popular
                        </span>
                      )}
                      {isCurrentPlan && (
                        <span className="absolute -top-3 right-4 px-3 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
                          Active
                        </span>
                      )}
                      <p className="font-semibold text-zinc-900 text-lg">{plan.name}</p>
                      <div className="flex items-baseline gap-1 mt-2 mb-1">
                        <span className="text-3xl font-bold text-zinc-900">${plan.price}</span>
                        <span className="text-zinc-500 text-sm">/month</span>
                      </div>
                      <p className="text-sm text-zinc-500 mb-3">{plan.credits} credits included</p>
                      <ul className="space-y-1.5 mb-4">
                        {plan.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-zinc-600">
                            <Check size={14} weight="bold" className="text-[#E91E8C] mt-0.5 shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={purchasingSub === plan.id || isCurrentPlan}
                        data-testid={`subscribe-${plan.id}`}
                        className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all ${
                          isCurrentPlan
                            ? "bg-emerald-100 text-emerald-700 cursor-default"
                            : plan.popular
                            ? "bg-[#E91E8C] text-white hover:bg-[#d4177d]"
                            : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200"
                        } disabled:opacity-50`}
                      >
                        {purchasingSub === plan.id ? "Redirecting..." : isCurrentPlan ? "Current Plan" : "Subscribe"}
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-100">
                <p className="text-sm text-zinc-600">
                  <Star size={14} weight="fill" className="text-amber-500 inline mr-1" />
                  <strong>Google Drive access</strong> unlocks with any subscription plan or when you have 100+ credits.
                  You can also purchase individual credit packs from the Credits tab.
                </p>
              </div>
            </>
          )}
          </div>  {/* /legacy hidden block */}
        </div>
      )}

      {/* Credits Tab */}
      {tab === "credits" && (
        <div className="space-y-6">
          {/* Balance card */}
          <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm mb-1">Your Credit Balance</p>
                <p className="text-4xl font-bold" data-testid="credit-balance">
                  {isAdmin ? "Unlimited" : (user?.credits ?? 0)}
                </p>
              </div>
              <Coins size={40} weight="duotone" className="text-[#E91E8C]" />
            </div>
            {isAdmin && (
              <div className="mt-3 flex items-center gap-2 text-sm text-emerald-400">
                <ShieldCheck size={16} weight="fill" />
                Admin — unlimited usage with your own API keys
              </div>
            )}
          </div>

          {pollSessionId && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-amber-800 text-sm font-medium">Processing your payment...</p>
            </div>
          )}

          {/* Credit packs */}
          {!isAdmin && (
            <>
              <h3 className="text-lg font-semibold text-zinc-900">Buy Credits</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {CREDIT_PACKS.map((pack) => (
                  <div
                    key={pack.id}
                    className={`relative border rounded-2xl p-5 transition-all hover:shadow-md ${
                      pack.popular ? "border-[#E91E8C] ring-1 ring-[#E91E8C]/20" : "border-zinc-200"
                    }`}
                  >
                    {pack.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#E91E8C] text-white text-xs font-bold rounded-full">
                        Most Popular
                      </span>
                    )}
                    <p className="font-semibold text-zinc-900 text-lg">{pack.name}</p>
                    <div className="flex items-baseline gap-1 mt-2 mb-1">
                      <span className="text-3xl font-bold text-zinc-900">${pack.price}</span>
                    </div>
                    <p className="text-sm text-zinc-500 mb-4">{pack.credits} credits</p>
                    <p className="text-xs text-zinc-400 mb-4">${(pack.price / pack.credits).toFixed(2)}/credit</p>
                    <button
                      onClick={() => handlePurchase(pack.id)}
                      disabled={purchasing === pack.id}
                      data-testid={`buy-${pack.id}`}
                      className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all ${
                        pack.popular
                          ? "bg-[#E91E8C] text-white hover:bg-[#d4177d]"
                          : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200"
                      } disabled:opacity-50`}
                    >
                      {purchasing === pack.id ? "Redirecting..." : "Buy Now"}
                    </button>
                  </div>
                ))}
              </div>

              {/* Credit costs reference */}
              <div className="bg-zinc-50 rounded-xl p-5 border border-zinc-100">
                <h4 className="font-semibold text-zinc-800 text-sm mb-3">Credit Costs</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                  {[
                    ["Auto Create", 15], ["Script", 1], ["Voice Clone", 3],
                    ["Video Gen", 10], ["Lip-Sync", 5], ["Outfit Change", 5],
                    ["Hook + Caption", 1], ["LinkedIn Post", 1], ["Image Prompt", 1],
                  ].map(([name, cost]) => (
                    <div key={name} className="flex justify-between items-center px-3 py-1.5 bg-white rounded-lg border border-zinc-100">
                      <span className="text-zinc-600">{name}</span>
                      <span className="font-semibold text-zinc-900">{cost}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Budget & Balance Tab */}
      {tab === "budget" && (
        <div className="space-y-6" data-testid="tab-budget-content">
          {usageLoading && !usage ? (
            <div className="flex items-center justify-center py-16 text-zinc-400">
              <CircleNotch size={22} className="animate-spin mr-2" /> Loading usage…
            </div>
          ) : !usage ? (
            <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-xl p-6 text-center">
              Couldn't load usage summary. <button onClick={loadUsage} className="text-[#E91E8C] font-semibold ml-1">Try again</button>
            </div>
          ) : (
            <>
              {/* Live balances */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-zinc-900 flex items-center gap-2"><Wallet size={20} weight="fill" className="text-[#E91E8C]" /> Live balances</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">Pulled directly from each provider. Click "refresh" any time.</p>
                  </div>
                  <button onClick={loadUsage} disabled={usageLoading} className="text-xs font-semibold text-[#E91E8C] hover:text-[#c91a7b] flex items-center gap-1" data-testid="budget-refresh-btn">
                    {usageLoading ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} weight="fill" />}
                    Refresh
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    ["fal_ai", "fal.ai (Fabric)", "⚡ PRIMARY · talking-head video (~1-2 min renders)"],
                    ["muapi", "muapi.ai", "Images · lifestyle · motion transfer"],
                    ["magic_hour", "Magic Hour", "Secondary · lip-sync · long-form fallback"],
                    ["elevenlabs", "ElevenLabs", "Voice cloning · TTS (free 10k chars/mo)"],
                  ].map(([id, name, desc]) => {
                    const b = usage.balances[id] || {};
                    const hasBal = typeof b.balance === "number";
                    return (
                      <div key={id} className="card p-4" data-testid={`budget-provider-${id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-bold text-zinc-900">{name}</p>
                            <p className="text-[11px] text-zinc-500">{desc}</p>
                          </div>
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            b.ok ? (b.source === "user" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700") : "bg-amber-100 text-amber-700"
                          }`}>
                            {b.ok ? (b.source === "user" ? "Your key" : "Platform") : "Missing"}
                          </span>
                        </div>
                        <div className="mt-3">
                          {hasBal ? (
                            <p className="text-3xl font-bold text-zinc-900">${b.balance?.toFixed(2)}</p>
                          ) : (
                            <p className="text-sm text-zinc-400 italic">Balance not exposed by provider</p>
                          )}
                          <p className="text-[11px] text-zinc-500 mt-1">{b.note}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Remaining capacity */}
              {usage.estimates && Object.keys(usage.estimates).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-zinc-900 flex items-center gap-2"><Gauge size={20} weight="fill" className="text-[#E91E8C]" /> What ${usage.balances.muapi?.balance?.toFixed(2)} can still make</h3>
                  <p className="text-xs text-zinc-500 mt-0.5 mb-3">Based on your remaining muapi balance and upstream unit prices.</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      ["images", "AI images", "@ $0.04 each"],
                      ["lifestyle_videos", "Lifestyle videos", "@ $0.30 each"],
                      ["motion_runway", "Motion · Runway", "@ $0.07 each"],
                      ["motion_kling_26", "Motion · Kling 2.6", "@ $0.145 each"],
                      ["motion_kling_30", "Motion · Kling 3.0", "@ $0.24 each"],
                      ["broll_clips", "B-roll clips", "@ $0.05 each"],
                    ].map(([id, label, unit]) => (
                      <div key={id} className="card p-3" data-testid={`budget-estimate-${id}`}>
                        <p className="text-2xl font-bold text-zinc-900">{usage.estimates[id]?.toLocaleString() ?? "—"}</p>
                        <p className="text-xs font-semibold text-zinc-700">{label}</p>
                        <p className="text-[10px] text-zinc-400">{unit}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lifetime spend */}
              <div>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-lg font-semibold text-zinc-900 flex items-center gap-2"><ChartLineUp size={20} weight="fill" className="text-[#E91E8C]" /> Lifetime spend</h3>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400">Estimated</span>
                </div>
                <p className="text-xs text-zinc-500 mt-0.5 mb-3">Computed from completed jobs × documented unit prices. Use it to budget — exact billing lives in each provider's dashboard.</p>

                <div className="card p-5">
                  <p className="text-4xl font-bold text-zinc-900" data-testid="budget-spent-total">${usage.spent.total_usd?.toFixed(2)}</p>
                  <p className="text-xs text-zinc-500 mt-1">Total across all providers</p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5">
                    {Object.entries(usage.spent.by_provider).map(([p, v]) => (
                      <div key={p} className="bg-zinc-50 border border-zinc-200 rounded-lg p-2.5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{p.replace("_", " ")}</p>
                        <p className="text-lg font-bold text-zinc-900">${v.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>

                  {Object.keys(usage.spent.by_feature).length > 0 && (
                    <div className="mt-5">
                      <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Breakdown by feature</p>
                      <div className="space-y-1.5">
                        {Object.entries(usage.spent.by_feature)
                          .sort((a, b) => b[1].subtotal - a[1].subtotal)
                          .map(([feat, d]) => (
                            <div key={feat} className="flex items-center justify-between text-xs border-b border-zinc-100 py-1.5" data-testid={`budget-feat-${feat}`}>
                              <span className="text-zinc-700 font-medium capitalize">{feat.replace(/_/g, " ")}</span>
                              <span className="text-zinc-500">{d.count} × ${(d.unit_cost ?? (d.count ? d.subtotal / d.count : 0)).toFixed(2)} = <span className="text-zinc-900 font-bold">${(d.subtotal ?? 0).toFixed(2)}</span></span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Base Video Library Tab */}
      {tab === "videos" && (
        <div className="space-y-4" data-testid="tab-videos-content">
          <BaseVideoLibrary />
        </div>
      )}

      {/* BYOK Tab */}
      {tab === "keys" && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
            <p className="font-medium mb-1">Bring Your Own Keys (BYOK)</p>
            <p className="text-blue-600">
              {isAdmin
                ? "Your admin keys are configured server-side. You can also add personal overrides here."
                : "Add your own API keys to use services without spending credits. Keys are encrypted and stored securely."}
            </p>
          </div>

          {Object.entries(SERVICE_LABELS).map(([service, info]) => {
            const hasKey = keys[service] && Object.values(keys[service]).some(v => v && v !== "");
            return (
              <div key={service} className="border border-zinc-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-zinc-900">{info.name}</p>
                    <p className="text-xs text-zinc-500">{info.icon}</p>
                    {info.url && (
                      <a href={info.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#E91E8C] hover:underline">{info.url}</a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {service === "elevenlabs" ? (() => {
                      const EL_MAP = {
                        validated: { label: "Connected", cls: "text-emerald-600" },
                        present_unvalidated: { label: "Stored — not validated", cls: "text-amber-600" },
                        invalid_permissions: { label: "Permissions required", cls: "text-red-600" },
                        quota_or_plan_issue: { label: "Quota/plan issue", cls: "text-amber-600" },
                        missing: { label: "Not connected", cls: "text-zinc-400" },
                      };
                      const st = elevenlabsStatus || (hasKey ? "present_unvalidated" : "missing");
                      const m = EL_MAP[st] || EL_MAP.missing;
                      return (
                        <span className={`flex items-center gap-1 text-xs font-medium ${m.cls}`} data-testid="elevenlabs-key-status">
                          {st === "validated" ? <Check size={14} weight="bold" /> : null} {m.label}
                        </span>
                      );
                    })() : (hasKey && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                        <Check size={14} weight="bold" /> Connected
                      </span>
                    ))}
                    {editingKey === service ? (
                      <button onClick={() => setEditingKey(null)} className="text-xs text-zinc-500 hover:text-zinc-700 px-2 py-1">
                        Cancel
                      </button>
                    ) : (
                      <button
                        onClick={() => setEditingKey(service)}
                        data-testid={`edit-key-${service}`}
                        className="text-xs text-[#E91E8C] font-medium hover:underline px-2 py-1"
                      >
                        {hasKey ? "Update" : "Add Key"}
                      </button>
                    )}
                    {hasKey && editingKey !== service && (
                      <button onClick={() => handleDeleteKey(service)} className="text-xs text-red-500 hover:text-red-700 p-1">
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {editingKey === service && (
                  <div className="mt-3 space-y-2">
                    {info.instructions && (
                      <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 mb-2">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">How to get your key</p>
                        {info.instructions.split("\n").map((line, i) => (
                          <p key={`inst-${i}-${line.slice(0,15)}`} className="text-xs text-zinc-600">{line}</p>
                        ))}
                      </div>
                    )}
                    {(service === "elevenlabs" ? ["api_key"] :
                      service === "magic_hour" ? ["api_key"] :
                      service === "fal_ai" ? ["api_key"] :
                      ["api_key"]
                    ).map((field) => (
                      <div key={field}>
                        <label className="text-xs text-zinc-500 font-medium capitalize">{field.replace("_", " ")}</label>
                        <input
                          type={field === "api_key" && !showKeys[`${service}_${field}`] ? "password" : "text"}
                          value={keyInputs[service]?.[field] || ""}
                          onChange={(e) => setKeyInputs(prev => ({
                            ...prev,
                            [service]: { ...prev[service], [field]: e.target.value }
                          }))}
                          placeholder={`Enter your ${field.replace("_", " ")}`}
                          data-testid={`key-input-${service}-${field}`}
                          className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30"
                        />
                      </div>
                    ))}
                    <button
                      onClick={() => handleSaveKey(service)}
                      disabled={savingKey === service}
                      data-testid={`save-key-${service}`}
                      className="px-4 py-2 bg-[#E91E8C] text-white text-sm font-semibold rounded-lg hover:bg-[#d4177d] disabled:opacity-50"
                    >
                      {savingKey === service ? "Saving..." : "Save Key"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
